import { Component, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  PaymentRulesState,
  PaymentStoreState,
  SessionState,
  SessionStore,
  selectContactInformation,
  selectIsAlternativePaymentMethodAvailable,
  selectIsPaymentSuccess,
  selectPayOtherAmountHasThrownError,
  selectShowContactInformation,
} from '@arc/unified-payments-frontend/shared-stores';
import { selectCurrentLanguage } from '@arc/unified-payments-frontend/i18n';
import {
  CheckoutStep,
  GetSessionSummaryResponse,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  PaymentFooterStoreState,
  selectCheckoutStep,
  PaymentFooterActions,
  selectTranslations,
} from '../../store';

@Component({
  selector: 'payment-footer-container',
  templateUrl: './payment-footer.container.component.html',
  styleUrls: ['./payment-footer.container.component.scss'],
})
export class PaymentFooterContainerComponent implements OnInit {
  currentStep$: Observable<CheckoutStep>;
  paymentSteps = CheckoutStep;
  sessionSummary$: Observable<GetSessionSummaryResponse>;
  subscriberAgreementUrl$: Observable<string>;
  paymentSucceeded$: Observable<boolean>;
  hasPayOtherAmountThrownAnError$: Observable<boolean>;
  translations$: Observable<Record<string, string>>;
  showContactInformation$: Observable<boolean>;
  contactInformation$: Observable<string>;
  isAlternativePaymentMethodAvailable$: Observable<boolean>;

  constructor(
    private store: Store<PaymentFooterStoreState>,
    private sessionStore: Store<SessionState>,
    private paymentStore: Store<PaymentStoreState>,
    private paymentRulesStore: Store<PaymentRulesState>,
  ) {}

  ngOnInit(): void {
    this.translations$ = this.store.select(selectTranslations);
    this.currentStep$ = this.store.pipe(select(selectCheckoutStep));
    this.sessionSummary$ = this.sessionStore.select(SessionStore);
    this.paymentSucceeded$ = this.store.select(selectIsPaymentSuccess);
    this.showContactInformation$ = this.store.select(
      selectShowContactInformation,
    );
    this.contactInformation$ = this.store.select(selectContactInformation);

    this.subscriberAgreementUrl$ = combineLatest([
      this.sessionSummary$.pipe(filter((s) => s != null)),
      this.store.select(selectCurrentLanguage),
    ]).pipe(
      map(([summary, language]) => {
        if (summary.subscriberAgreementUrl != null) {
          return summary.subscriberAgreementUrl;
        }

        if (summary.isBuyerOriginApp) {
          return `subscriber-agreement?lang=${language}`;
        }

        return null;
      }),
    );

    this.hasPayOtherAmountThrownAnError$ = this.paymentStore.select(
      selectPayOtherAmountHasThrownError,
    );

    this.isAlternativePaymentMethodAvailable$ = this.paymentRulesStore.select(
      selectIsAlternativePaymentMethodAvailable,
    );
  }

  public onSessionExitClicked(): void {
    this.store.dispatch(PaymentFooterActions.ExecuteSessionExitAction());
  }

  public onProceedToPaymentClicked() {
    this.store.dispatch(PaymentFooterActions.ExecuteProceedToPaymentAction());
  }

  public onBackFromSelectPaymentTypeClicked() {
    this.store.dispatch(
      PaymentFooterActions.ExecuteBackFromSelectPaymentTypeAction(),
    );
  }

  public onBackFromSelectPaymentMethodClicked() {
    this.store.dispatch(
      PaymentFooterActions.ExecuteBackFromSelectPaymentMethodAction(),
    );
  }

  public onBackFromConfirmPaymentClicked() {
    this.store.dispatch(
      PaymentFooterActions.ExecuteBackFromConfirmPaymentAction(),
    );
  }

  public onReviewPaymentClicked(): void {
    this.store.dispatch(PaymentFooterActions.ExecuteReviewPaymentAction());
  }

  public onBackFromReviewPaymentClicked() {
    this.store.dispatch(
      PaymentFooterActions.ExecuteBackFromReviewPaymentAction(),
    );
  }

  public onAuthorizePaymentClicked(): void {
    this.store.dispatch(PaymentFooterActions.ExecuteAuthorizePaymentAction());
  }

  public onReturnToAppClicked(): void {
    this.store.dispatch(PaymentFooterActions.ExecuteReturnToAppAction());
  }
}
